/**
 * ------ maintenance history ------
 * 2020/01/15 Ella Ma Add Default home layout in home screen.
 */

import { Component, OnInit, OnDestroy, ViewChild, HostListener } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { Store, createSelector, select } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { take, filter } from 'rxjs/operators';
import { SaveScreenDialogService } from './save-screen-dialog.service';
import { SaveScreenDialogOpenParam, SaveScreenDialogOperations, SaveScreenDialogType, SaveScreenDialogInvoker } from './save-screen-dialog.view-model';
import { DualListConfig, DualListEventType } from '../../widgets/dual-list/dual-list.config';
import { StoreQuerierService } from '../../services/store-querier.service';
import { DualListItem } from '../../widgets/dual-list/dual-list.component';
import { AlertWindowService } from '../../widgets/alert-window/alert-window.service';
import { AppState } from '../../redux';
import { ScreenService } from '../../tamalelibs/services/screen.service';
import { SavedScreen } from '../../tamalelibs/models/saved-screen.model';
import { Router } from '@angular/router';
import { ToastService } from '../../widgets/toast/toast.service';
import { NotificationStyles } from '../../widgets/notification/notification.model';
import { SystemUser } from '../../tamalelibs/models/user.model';
import { SystemActionTypes } from '../../tamalelibs/redux/actions/system.actions';
import { HomeScreenService } from '../../services/home-screen.service';
import { businessConstants } from '../../constants/business.constants';
import { ScreenActionTypes } from '../../redux/actions/screen.actions';
import { HomeScreenActionTypes } from '../../redux/actions/home-screen.actions';
import { userSettingSelector } from '../../redux/reducers/users.reducer';
import { UsersActionTypes } from '../../redux/actions/users.actions';

@Component({
    selector: 'save-screen-dialog',
    templateUrl: './save-screen-dialog.component.html',
    styleUrls: ['./save-screen-dialog.component.scss']
})
export class SaveScreenDialogComponent implements OnInit, OnDestroy {

    @ViewChild('dualList', { static: false }) dualListInst;

    currentUser: SystemUser;
    dualListConfig: DualListConfig;
    isOpen = false;
    isSaving = false;
    pageTitle: string;
    pageType: string;
    screenData: SavedScreen;
    saveBtnStatus = false;
    homeScreenData: any; // Add a param for homescreen
    screenForm = new UntypedFormGroup({
        name: new UntypedFormControl(''),
        description: new UntypedFormControl(''),
        isPublic: new UntypedFormControl({ value: true, disabled: false }),
        defaultHomeLayout: new UntypedFormControl(false)
    });
    showDefaultLayout = false;

    private _operation: SaveScreenDialogOperations;
    private _isHomeScreen = false;
    private _isManuallyChange = true;
    private _originalName: string;
    private _originalDescription: string;
    private _originalIsPublic: boolean;
    private _originalDefaultHomeLayout = false;
    private _saveScreenInvoker: string;

    get name() {
        return this.screenForm.get(businessConstants.dialogInfo.name);
    }

    get description() {
        return this.screenForm.get(businessConstants.dialogInfo.description);
    }

    get isPublic() {
        return this.screenForm.get(businessConstants.dialogInfo.isPublic);
    }

    get defaultHomeLayout() {
        return this.screenForm.get(businessConstants.dialogInfo.defaultHomeLayout);
    }

    private _destroySubscriptions: Array<Subscription> = [];
    private _hadModifyDualList = false;

    constructor(
        private _dialogService: SaveScreenDialogService,
        private _router: Router,
        private _screenService: ScreenService,
        private _store: Store<AppState>,
        private _storeQuerier: StoreQuerierService,
        private _toast: ToastService,
        private _alertWindow: AlertWindowService,
        private _homeScreenService: HomeScreenService
    ) { }

    ngOnInit() {
        this.currentUser = this._storeQuerier.getCurrentUser();
        this.initComponent();

        const accessListSelector = createSelector(
            (state: AppState) => state.users,
            (state: AppState) => state.teams,
            (allUser, allTeam) => {
                if (allUser.length === 0 && allTeam.length === 0) {
                    return [];
                }
                const users = allUser.map(item => {
                    return new DualListItem(item);
                });
                const teams = allTeam.map(item => {
                    return new DualListItem(Object.assign({ type: businessConstants.entity.entityType.Team }, item));
                });
                return users.concat(teams);
            }
        );

        this._destroySubscriptions.push(
            this._dialogService.open$.subscribe((param: SaveScreenDialogOpenParam) => this._dialogOpenHandler(param)),
        );

        this._destroySubscriptions.push(
            this._store.pipe(
                select(accessListSelector),
                filter((list: any) => list.length > 0),
            ).subscribe(list => {
                this.dualListConfig.dataSource = list;
            })
        );
        this._destroySubscriptions.push(
            this.dualListConfig.feedbackSubject$.pipe(
                filter((item) => item.type === DualListEventType.selectChange)
            ).subscribe(() => {
                this._hadModifyDualList = true;
                this.saveBtnStatus = false;
            })
        );
        // handle value change to change the save button status
        this.screenForm.valueChanges.subscribe(data => {
            if (this._isManuallyChange) {
                if (data.name !== this._originalName ||
                    data.description !== this._originalDescription ||
                    data.isPublic !== this._originalIsPublic ||
                    data.defaultHomeLayout !== this._originalDefaultHomeLayout) {
                    this.saveBtnStatus = false;
                } else {
                    this.saveBtnStatus = true;
                }
            }
        });
    }

    ngOnDestroy() {
        this._destroySubscriptions.forEach(subscription => subscription.unsubscribe());
    }

    initComponent() {
        this.dualListConfig = new DualListConfig();
        this.dualListConfig.titles = [businessConstants.dualList.userNotAllowedAccessTitle, businessConstants.dualList.userAllowedAccessTitle];
        this.dualListConfig.placeholderText = businessConstants.dualList.placeHolderText;
        this.dualListConfig.dataTextField = businessConstants.dualList.dataTextField;
        this.dualListConfig.dataValueField = businessConstants.dualList.dataValueField;
    }

    @HostListener('window:beforeunload', ['$event'])
    beforeunloadHandler($event: any) {
        if (this.isOpen) {
            $event.returnValue = true;
        }
    }

    onCancelClick() {
        if (this._hadModifyDualList || this.screenForm.dirty) {
            const subscription = this._alertWindow.warn(businessConstants.alertWindow.warnTitle, [businessConstants.alertWindow.warnContent])
                .subscribe((result: boolean) => {
                    if (result) {
                        this._closeDialog();
                    }
                    subscription.unsubscribe();
                });
        } else {
            this._closeDialog();
        }
    }

    onSaveClick() {
        this._doValidation();

        if (this.screenForm.invalid) {
            return;
        }

        this.isSaving = true;
        if (this._isHomeScreen) {
            this.homeScreenData.name = this.name.value;
            this.homeScreenData.description = this.description.value;
            this.homeScreenData.isPublic = this.isPublic.value;
            this.homeScreenData.accessList = this.dualListInst.getSelectedItem(true);
            this.homeScreenData.defaultHomeLayout = this.defaultHomeLayout.value;
            if (this._operation === SaveScreenDialogOperations.CREATE) {
                this._homeScreenService.createNewScreenLayout(this.homeScreenData).subscribe(response => this._createdSuccessHandler(response), response => this._failedHandler(response));
            } else {
                this._homeScreenService.updateOriginalHomeScreen(this.homeScreenData).subscribe(response => this._editedSuccessHandler(response), response => this._failedHandler(response));
            }
        } else {
            this.screenData.name = this.name.value;
            this.screenData.description = this.description.value;
            this.screenData.isPublic = this.isPublic.value;
            this.screenData.accessList = this.dualListInst.getSelectedItem(true);
            if (this._operation === SaveScreenDialogOperations.CREATE) {
                this._screenService.create(this.screenData).pipe(
                    take(1)
                ).subscribe(response => this._saveSuccessHandler(response, this._operation), response => this._failedHandler(response));
            } else if (this._operation === SaveScreenDialogOperations.EDIT) {
                this._screenService.update(this.screenData).pipe(
                    take(1)
                ).subscribe(response => this._saveSuccessHandler(response, this._operation), response => this._failedHandler(response));
            }
        }
    }

    private _closeDialog() {
        this.isOpen = false;
        this._reset();
    }

    private _doValidation() {
        this.name.setErrors(null);
        this.description.setErrors(null);

        if (!this.name.value) {
            this.name.setErrors({
                required: true
            });
        } else if (this.name.value.length > 128) {
            this.name.setErrors({
                maxlength: true
            });
        }

        if (this.description && this.description.value) {
            if (this.description.value.length > 256) {
                this.description.setErrors({
                    maxlength: true
                });
            }
        }
    }

    private _dialogOpenHandler(param: SaveScreenDialogOpenParam) {
        if (param.operation === SaveScreenDialogOperations.CREATE && param.type === SaveScreenDialogType.RESEARCH_VIEW) {
            this._isHomeScreen = false;
            this.saveBtnStatus = false;
            this.isPublic.enable();
            this.pageTitle = businessConstants.research.saveScreen.createTitle;
            this.pageType = businessConstants.research.saveScreen.type;
            this.name.setValue('');
            this.description.setValue('');
            if (this.currentUser.credential.publishPubConfiguration) {
                this.isPublic.setValue(true);
                this.isPublic.enable();
            } else {
                this.isPublic.setValue(false);
                this.isPublic.disable();
            }
            setTimeout(() => {
                this.dualListInst.setSelectedItem(this.currentUser.id, false);
            });
            this.screenData = {
                configuration: param.data,
                isPublic: this.isPublic.value,
                isLandingPage: false,
                visible: true,
                type: 'research',
            };
        } else if (param.operation === SaveScreenDialogOperations.EDIT && param.type === SaveScreenDialogType.RESEARCH_VIEW) {
            this._isHomeScreen = false;
            this.saveBtnStatus = true;
            this._isManuallyChange = false;
            this.pageTitle = businessConstants.research.saveScreen.editTitle;
            this.pageType = businessConstants.research.saveScreen.type;
            this._originalName = param.data.name;
            this._originalDescription = param.data.description;
            this.name.setValue(param.data.name);
            this.description.setValue(param.data.description);

            if (this.currentUser.credential.publishPubConfiguration) {
                this.isPublic.setValue(param.data.isPublic);
                this.isPublic.enable();
            } else {
                this.isPublic.setValue(false);
                this.isPublic.disable();
            }
            setTimeout(() => {
                if (param.data.accessList) {
                    this.dualListInst.setSelectedItem(param.data.accessList);
                    this._originalIsPublic = false;
                }
                this._isManuallyChange = true;
            });
            this.screenData = param.data;
            this.screenData.type = 'research';
            this._saveScreenInvoker = param.invoker;
        } else if (param.operation === SaveScreenDialogOperations.CREATE && param.type === SaveScreenDialogType.HOME_SCREEN) {
            this._isHomeScreen = true;
            this.saveBtnStatus = false;
            this.pageTitle = businessConstants.homeScreen.saveLayout.createTitle;
            this.pageType = businessConstants.homeScreen.saveLayout.type;
            this.name.setValue(param.data.name);
            this.description.setValue(param.data.description);
            this.isPublic.setValue(true);
            if (param.data.id === businessConstants.defaultHomeScreenId) {
                this.isPublic.disable();
            } else {
                this.isPublic.enable();
            }
            this.homeScreenData = param.data;
            setTimeout(() => {
                this.dualListInst.setSelectedItem(this.currentUser.id, false);
            });
        } else if (param.operation === SaveScreenDialogOperations.EDIT && param.type === SaveScreenDialogType.HOME_SCREEN) {
            this._isHomeScreen = true;
            this.saveBtnStatus = false;
            this.pageTitle = businessConstants.homeScreen.saveLayout.editTitle;
            this.pageType = businessConstants.homeScreen.saveLayout.type;
            this.name.setValue(param.data.name);
            this.description.setValue(param.data.description);
            if (param.data.id === businessConstants.defaultHomeScreenId) {
                this.isPublic.setValue(true);
                this.isPublic.disable();
            } else {
                this.isPublic.enable();
                this.isPublic.setValue(param.data.isPublic);
                setTimeout(() => {
                    this.dualListInst.setSelectedItem(param.data.accessList);
                });
            }
            this.homeScreenData = param.data;
        }
        this.showDefaultLayout = this._isHomeScreen && this.currentUser.credential.publishPubConfiguration;
        this._operation = param.operation;
        this.isOpen = true;
    }

    private _reset() {
        this._hadModifyDualList = false;
        this.saveBtnStatus = true;
        this.screenForm.reset();
        this.dualListConfig.dataSource.forEach(item => {
            item.direction = businessConstants.common.position.left;
        });
    }

    private _saveSuccessHandler(response, operation) {
        this.isSaving = false;
        this._closeDialog();

        if (operation === SaveScreenDialogOperations.CREATE) {
            setTimeout(() => {
                this._toast.notify({
                    message: response.name + businessConstants.notification.saveScreen.createSuccessfullyMsg,
                    style: NotificationStyles.Success
                });
            });
        } else if (operation === SaveScreenDialogOperations.EDIT) {
            setTimeout(() => {
                this._toast.notify({
                    message: response.name + businessConstants.notification.saveScreen.editSuccessfullyMsg,
                    style: NotificationStyles.Success
                });
            });
        }

        if (this._saveScreenInvoker !== SaveScreenDialogInvoker.MANAGE_SCREENS) {
            this._router.navigate([businessConstants.screenType.research, response.id]);
        }

        if (this._operation === SaveScreenDialogOperations.CREATE || this._operation === SaveScreenDialogOperations.COPY) {
            this._store.dispatch({
                type: SystemActionTypes.ADD_SCREEN,
                payload: {
                    type: businessConstants.screenType.research,
                    ...response
                },
            });
        } else if (this._operation === SaveScreenDialogOperations.EDIT) {
            this._store.dispatch({
                type: SystemActionTypes.EDIT_SCREEN,
                payload: {
                    type: businessConstants.screenType.research,
                    ...response
                },
            });
        }

        // store the saved screen info to avoid to send duplicate request in home screen
        this._store.dispatch({
            type: ScreenActionTypes.SCREEN_DICTIONARY,
            payload: response
        });
    }

    private _failedHandler(error) {
        this.isSaving = false;
        if (error.message.includes(businessConstants.errorMessage.ERR_NAME_EXISTED)) {
            this.name.setErrors({
                duplicated: true
            });
        }
    }

    private _editedSuccessHandler(response) {
        const currentPageInfo: any = {
            id: response.id,
            name: response.name,
            type: businessConstants.homeScreen.pageTypeText
        };
        this._store.dispatch({
            type: ScreenActionTypes.SET_VALUE,
            payload: currentPageInfo
        });
        this._store.dispatch({ type: ScreenActionTypes.SCREEN_STATUS_DICTIONARY_CLEAR });

        this.isSaving = false;
        this._closeDialog();
        this._toast.notify({
            message: response.name + businessConstants.notification.homeScreen.editSuccessfullyMsg,
            style: NotificationStyles.Success
        });
        this._updateUserSettingStore(response.id);
    }

    private _createdSuccessHandler(response) {
        const currentPageInfo: any = {
            id: response.id,
            name: response.name,
            type: businessConstants.homeScreen.pageTypeText
        };
        this._store.dispatch({
            type: ScreenActionTypes.SET_VALUE,
            payload: currentPageInfo
        });

        this._store.dispatch({
            type: HomeScreenActionTypes.LOAD,
            payload: businessConstants.homeScreen.newLayoutName
        });

        this._store.dispatch({
            type: HomeScreenActionTypes.SHARE_LAYOUT,
            payload: response.id
        });

        this.isSaving = false;
        this._closeDialog();
        this._toast.notify({
            message: response.name + businessConstants.notification.homeScreen.createSuccessfullyMsg,
            style: NotificationStyles.Success
        });
        this._updateUserSettingStore(response.id);
    }

    private _updateUserSettingStore(layoutId) {
        if (this.homeScreenData.defaultHomeLayout) {
            const userSetting = this._storeQuerier.queryBySelector(userSettingSelector);
            if (userSetting.defaultHomeLayout) {
                userSetting.defaultHomeLayout.configuration.id = layoutId;
            } else {
                userSetting['defaultHomeLayout'] = {
                    configuration: {
                        id: layoutId
                    }
                };
            }
            this._store.dispatch({
                type: UsersActionTypes.UPDATE_USER_SETTING_SUCCESS,
                payload: userSetting
            });
        }
    }
}
